import * as React from "react";
import {useContext, useEffect, useState} from "react";
import {IfBox} from "../../../../../style/if";
import {Button, Dropdown, DropdownItemProps} from "semantic-ui-react";
import Notifications, {notify} from 'react-notify-toast';
import {ExportSimAddFilesToFTPModal} from "./ExportSimAddFilesToFTPModal";
import {ExportSimSaveAsTestModal} from "../ExportSimSaveAsTestModal";
import {DownloadButton} from "../../../../../util/react-download-link/DownloadButton";
import {FileViewer} from "../../../../../util/fileViewer/FileViewer";
import {workingConfig} from "../../../configChange/ConfigSignal";
import {
    exportSimErrors,
    exportSimOutputIndex,
    exportSimOutputs,
    getCurrentExportSimOutput
} from "../input/ExportSimPrepareSide";
import {sleep} from "../../../../../../utils";
import {exportSimTransportOrders} from "../input/ExportSimTransportOrderEditor";
import {ConfigContext} from "../../../../context/ConfigContext";
import {useHistory} from "react-router-dom";
import {findDisplayName} from "../../../configChange/configEdit/newNavTree/NavTree";


export interface ExportSimOutputProps {
    placeholder: string,
    loading: boolean,
}

export function ExportSimOutput(props: React.PropsWithChildren<ExportSimOutputProps>) {
    const context = useContext(ConfigContext)
    const history = useHistory()

    const [outputFileIndex, setOutputFileIndex] = useState(0)
    const [showAddFileModal, setShowAddFileModal] = useState(false)
    const [showSaveAsTestModal, setShowSaveAsTestModal] = useState(false)
    const [selectedFile, setSelectedFile] = useState(null)
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        setLoading(props.loading)
    }, [props.loading])

    useEffect(() => {
        setOutputFileIndex(0)
        selectOutputFile(0)
    }, [])

    useEffect(() => {
        selectOutputFile(0)
    }, [exportSimOutputs.value, exportSimOutputIndex.value])


    function getExportSimOutputFileByIndex(index: number = outputFileIndex) {
        let output = getCurrentExportSimOutput()
        if (output != "" &&
            output != "[]" &&
            output != "204 - No Content" &&
            output != null
        ) {
            try {
                let parsedOutput = JSON.parse(output)
                let files = parsedOutput?.resultFiles
                let file = files[index]
                if (!file) {
                    console.log("[getExportSimOutputFileByIndex] No file found at index ", index)
                }
                return file
            } catch (e) {
                console.log("ERROR: ", e)
                return null
            }
        } else {
            return null
        }
    }

    function selectOutputFile(newOutputFileIndex = outputFileIndex) {
        setSelectedFile(null)
        sleep(100).then(() => {
            if (!outputEmpty()) {
                setOutputFileIndex(newOutputFileIndex)
                let file = getExportSimOutputFileByIndex(newOutputFileIndex)
                setSelectedFile(file)
            }
        })
    }

    function fileAmountToArray() {
        let enumValues: Array<DropdownItemProps> = [];

        if (!outputEmpty()) {
            try {
                let output = JSON.parse(getCurrentExportSimOutput())

                for (let i = 0; i < output.resultFiles.length; i++) {
                    let name = output.resultFiles[i]?.fileName
                    enumValues.push({key: name, value: i, text: name});
                }
            } catch (e) {
                console.log("ERROR: ", e)
            }
        }
        return enumValues
    }

    function getFileName() {
        try {
            JSON.parse(getCurrentExportSimOutput())
        } catch (e) {
            return "broken JSON"
        }
        if (getCurrentExportSimOutput()) {
            return JSON.parse(getCurrentExportSimOutput()).resultFiles[outputFileIndex]?.fileName
        } else {
            return "N/A"
        }

    }

    function prepareFileToDownload(file: any) {
        if (file.fileName?.endsWith(".pdf")) {
            const byteCharacters = atob(file.base64Content);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            return new Blob([byteArray], {type: "application/pdf"})
        }

        const byteCharacters = atob(file.base64Content);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        return new Blob([byteArray], {type: "application/octet-stream;"})
    }

    function readConverterMeta() {
        if (outputEmpty()) return null

        try {
            const output = JSON.parse(getCurrentExportSimOutput())
            return {
                converter: output.activeConverter || null,
                specificConfig: output.activeSpecificConfig || null,
            }
        } catch (e) {
            return null
        }
    }

    function toNamedPairs(obj: any): Array<{ name: string, value: any }> {
        if (!obj || typeof obj !== "object") return []
        return Object.keys(obj).map((key) => ({name: key, value: obj[key]}))
    }

    function findSpecificConfigPathByDisplayName(
        config: any,
        activeSpecificConfig: string
    ): string | null {
        if (!config || !activeSpecificConfig) return null
        const normalizedTarget = activeSpecificConfig.trim().toLowerCase()
        if (!normalizedTarget) return null

        const arrayKeys = new Set(["specificConfigurations", "specificConfiguration"])
        let foundPath: string | null = null

        const walk = (node: any, path: string) => {
            if (foundPath || node == null) return
            if (Array.isArray(node)) {
                node.forEach((item, index) => {
                    walk(item, `${path}.${index}`)
                })
                return
            }
            if (typeof node !== "object") return

            Object.keys(node).forEach((key) => {
                if (foundPath) return

                const value = node[key]
                const keyPath = `${path}.${key}`
                if (arrayKeys.has(key) && Array.isArray(value)) {
                    value.forEach((item, index) => {
                        if (foundPath || !item || typeof item !== "object") return
                        const displayName = findDisplayName(toNamedPairs(item))
                        if (displayName?.toString().trim().toLowerCase() === normalizedTarget) {
                            foundPath = `${keyPath}.${index}`
                        }
                    })
                }

                walk(value, keyPath)
            })
        }

        walk(config, "$")
        return foundPath
    }

    function collectScriptPaths(node: any, pathPrefix: string, accumulator: string[] = []): string[] {
        if (node == null) return accumulator

        if (typeof node === "string") {
            if (pathPrefix.toLowerCase().includes("script")) {
                accumulator.push(pathPrefix)
            }
            return accumulator
        }

        if (Array.isArray(node)) {
            node.forEach((item, index) => collectScriptPaths(item, `${pathPrefix}.${index}`, accumulator))
            return accumulator
        }

        if (typeof node === "object") {
            Object.keys(node).forEach((key) => {
                collectScriptPaths(node[key], `${pathPrefix}.${key}`, accumulator)
            })
        }

        return accumulator
    }

    function rankScriptPath(path: string) {
        let score = 0
        const lower = path.toLowerCase()
        if (lower.includes(".override.export.")) score += 100
        if (lower.includes("export")) score += 40
        if (lower.includes("transportorder")) score += 30
        if (lower.includes("queue")) score += 15
        if (lower.endsWith("script")) score += 10
        return score
    }

    function resolveScriptPathForPlayground(activeSpecificConfig: string): string | null {
        try {
            const config = JSON.parse(workingConfig.value)
            const specificConfigPath = findSpecificConfigPathByDisplayName(config, activeSpecificConfig)
            if (specificConfigPath) {
                const jp = require('jsonpath')
                const specificConfigObject = jp.value(config, specificConfigPath)
                const candidates = collectScriptPaths(specificConfigObject, specificConfigPath)
                    .sort((a, b) => rankScriptPath(b) - rankScriptPath(a))
                if (candidates.length > 0) {
                    return candidates[0]
                }
            }

            const globalCandidates = collectScriptPaths(config, "$")
                .filter((path) => path.toLowerCase().includes(".override.export."))
                .sort((a, b) => rankScriptPath(b) - rankScriptPath(a))
            if (globalCandidates.length === 1) {
                return globalCandidates[0]
            }

            return null
        } catch (e) {
            console.error("Unable to resolve playground script path", e)
            return null
        }
    }

    function openInScriptConverterPlayground() {
        const converterMeta = readConverterMeta()
        if (!converterMeta?.specificConfig) {
            notify.show("Active config is missing in converter response.", 'warning', 4000)
            return
        }
        if (!context?.companyId || !context?.setSingleTabSession || !context?.setScriptConverterPlaygroundScriptPath) {
            notify.show("Unable to open Script Converter Playground right now.", 'error', 4000)
            return
        }

        const scriptPath = resolveScriptPathForPlayground(converterMeta.specificConfig)
        if (!scriptPath) {
            notify.show(`No matching script path found for active config "${converterMeta.specificConfig}".`, 'warning', 5000)
            return
        }

        const jp = require('jsonpath')
        const config = JSON.parse(workingConfig.value)
        const script = jp.value(config, scriptPath)
        if (typeof script !== "string") {
            notify.show(`Resolved path does not contain a script: ${scriptPath}`, 'warning', 5000)
            return
        }

        const currentTransportOrder = exportSimTransportOrders.value[exportSimOutputIndex.value]
        const transferData = {
            transportOrderJson: currentTransportOrder ? JSON.stringify(currentTransportOrder, null, 4) : null,
            scriptPath,
            script,
        }
        localStorage.setItem('scriptConverterPlaygroundTransfer', JSON.stringify(transferData))
        context.setScriptConverterPlaygroundScriptPath(scriptPath)
        context.setSingleTabSession("configTab", 3, null, null)
        history.push(`/${context.companyId}/config`)
    }

    function renderConverterInfo() {
        if (outputEmpty()) return null;

        const meta = readConverterMeta()
        if (!meta) return null

        const converter = meta.converter || "Unknown"
        const config = meta.specificConfig || "Unknown"

        return (
            <div style={{
                display: "flex",
                gap: "15px",
                padding: "8px 12px",
                backgroundColor: "#fff",
                border: "1px solid rgba(34,36,38,.15)",
                borderRadius: "4px",
                fontSize: "13px",
                color: "rgba(0,0,0,.87)",
                alignItems: "center"
            }}>
                <span><strong>Converter:</strong> {converter}</span>
                <span><strong>Active Config:</strong> {config}</span>
                <Button
                    icon="arrow right"
                    size="mini"
                    title="Open active config script in Script Converter Playground"
                    onClick={openInScriptConverterPlayground}
                />
            </div>
        )
    }

    function drawButtons() {
        return <div style={{flexDirection: "row", display: "flex", justifyContent: "space-between", alignItems: "center"}}>
            <div style={{display: "flex", alignItems: "center", gap: "10px"}}>
                <IfBox shouldShow={!outputEmpty()}>
                    <Dropdown
                        selectOnBlur={false}
                        readOnly={false}
                        options={fileAmountToArray()}
                        selection
                        compact
                        value={outputFileIndex}
                        onChange={(evt, data) => selectOutputFile(data.value as number)}
                        style={{zIndex: 9999}}
                    />
                </IfBox>
                {renderConverterInfo()}
            </div>

            <div>
                {!outputEmpty() && getExportSimOutputFileByIndex(outputFileIndex) &&
                    <DownloadButton
                        filename={getFileName()}
                        exportFile={prepareFileToDownload(getExportSimOutputFileByIndex(outputFileIndex))}
                    />
                }
                <Button
                    style={{paddingTop: "-9px"}}
                    disabled={outputEmpty()}
                    onClick={(_) => setShowAddFileModal(true)}
                >
                    Add files to FTP
                </Button>
                <Button disabled={outputEmpty()}
                        onClick={(_) => setShowSaveAsTestModal(true)}
                >
                    Save as Test
                </Button>
            </div>
        </div>
    }

    function base64ToArrayBuffer(base64: string): ArrayBuffer {
        const binaryString = atob(base64);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }
        return bytes.buffer;
    }

    function drawEditor() {

        let error = exportSimErrors.value[exportSimOutputIndex.value]
        if(error) {
            return <p style={{color: "red"}}>Error: {error}</p>
        }
        if (selectedFile) {
            let key = "exportSimOutputFileViewer_" + exportSimOutputIndex.value + "_" + outputFileIndex
            return <FileViewer
                key={key}
                fileBuffer={base64ToArrayBuffer(selectedFile.base64Content)}
                fileName={selectedFile.fileName}
                displayName={false}
            />
        }
        if(loading){
            return <p>Loading...</p>
        }
        return <p>No file</p>
    }

    function addModals() {
        return <>
            <IfBox shouldShow={showAddFileModal}>
                <ExportSimAddFilesToFTPModal
                    isOpen={showAddFileModal}
                    onClose={(success, path) => {
                        if (success == true) {
                            notify.show('File has been added to ' + path, 'success', 3000, '#28f751');
                        }
                        setShowAddFileModal(false)
                    }
                    }
                />
            </IfBox>

            <IfBox shouldShow={showSaveAsTestModal}>
                <ExportSimSaveAsTestModal
                    isOpen={showSaveAsTestModal}
                    onClose={() => {
                        setShowSaveAsTestModal(false)
                    }}
                    eventType={null} // only used for importSim
                    expectedResult={getCurrentExportSimOutput()}
                    newStatus={null} // only used for importSim
                    transportOrder={exportSimTransportOrders.value[exportSimOutputIndex.value]} // only used for importSim
                    telematicState={null} // only used for importSim
                />

            </IfBox>
            <Notifications/>
        </>
    }

    return <>
        {drawButtons()}
        {drawEditor()}
        {addModals()}
    </>
}

export function outputEmpty() {
    let output = getCurrentExportSimOutput()
    return output == null || output == "" || output == "204 - No Content"
}